import type { TranslationKey } from '@/i18n/i18n';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

/**
 * Generic mutation hook with automatic cache invalidation
 * Reusable across all API modules
 */
export const useInvalidatingMutation = <TData = void, TVariables = void>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  invalidateQueryKey: readonly unknown[],
  errorMessage: TranslationKey,
  successMessage?: TranslationKey
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<TData, Error, TVariables>({
    mutationFn,
    onSuccess: () => {
      if (successMessage) toast.success(t(successMessage));
      void queryClient.invalidateQueries({ queryKey: invalidateQueryKey });
    },
    onError: (error) => {
      console.error(errorMessage, error);
      toast.error(t(errorMessage));
    }
  });
};
